import React, { useState, useEffect } from 'react';
import { invoicesAPI, jobsAPI, quotesAPI, emailAPI, eaAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Edit, Send, FileText, Trash2, CheckCircle, Download, Eye, X, FileSpreadsheet, Upload, Loader2, Calculator, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusColors = {
  draft: 'status-draft',
  sent: 'status-sent',
  paid: 'status-paid',
  partial: 'status-sent',
  overdue: 'status-overdue',
  cancelled: 'status-cancelled',
};

export const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [payMatches, setPayMatches] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [applyingPay, setApplyingPay] = useState(null);
  const [formData, setFormData] = useState({
    job_id: '',
    quote_id: '',
    client_name: '',
    client_email: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    due_date: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, jobsRes, quotesRes] = await Promise.all([
        invoicesAPI.getAll(),
        jobsAPI.getAll(),
        quotesAPI.getAll()
      ]);
      setInvoices(invoicesRes.data);
      setJobs(jobsRes.data);
      setQuotes(quotesRes.data);
      eaAPI.paymentMatches().then((r) => setPayMatches(r.data || [])).catch(() => {});
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleJobChange = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setFormData({
      ...formData,
      job_id: jobId,
      client_name: job?.client_name || '',
      client_email: job?.client_email || '',
    });
  };

  const handleQuoteChange = (quoteId) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (quote) {
      setFormData({
        ...formData,
        quote_id: quoteId,
        job_id: quote.job_id,
        client_name: quote.client_name,
        client_email: quote.client_email || '',
        items: quote.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
      });
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await invoicesAPI.create(formData);
      toast.success('Invoice created');
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleImportInvoice = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await invoicesAPI.importInvoice(fd);
      setImportResult(res.data);
      toast.success(res.data.matched ? `Matched to ${res.data.job_title}` : 'Imported — please review the match');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not import invoice');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const handleApplyPayment = async (id) => {
    setApplyingPay(id);
    try {
      const res = await eaAPI.applyPaymentMatch(id);
      const n = res.data?.updated?.length || 0;
      toast.success(n ? `Marked ${n} invoice(s) as paid` : 'Payment reconciled');
      setPayMatches((prev) => prev.filter((p) => p.id !== id));
      fetchData();
    } catch (error) {
      toast.error('Could not reconcile this payment');
    } finally {
      setApplyingPay(null);
    }
  };

  const handleDismissPayment = async (id) => {
    try {
      await eaAPI.dismissPaymentMatch(id);
      setPayMatches((prev) => prev.filter((p) => p.id !== id));
      toast.success('Dismissed');
    } catch (error) {
      toast.error('Could not dismiss');
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await invoicesAPI.update(invoiceId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await invoicesAPI.downloadPdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleViewPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await invoicesAPI.downloadPdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(`Invoice ${invoiceNumber}`);
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error('Failed to load PDF');
    }
  };

  const handleExportExcel = async (invoiceId, invoiceNumber) => {
    try {
      const response = await invoicesAPI.exportExcel(invoiceId);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoiceNumber}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exported');
    } catch (error) {
      toast.error('Failed to export Excel');
    }
  };

  const handleSendInvoice = async (invoice) => {
    if (!invoice.client_email) {
      toast.error('No client email address');
      return;
    }
    
    const htmlContent = `
      <h2>Invoice from My Project Agent</h2>
      <p>Dear ${invoice.client_name},</p>
      <p>Please find your invoice <strong>${invoice.invoice_number}</strong>.</p>
      <p><strong>Total Due: $${invoice.total?.toFixed(2)}</strong></p>
      ${invoice.due_date ? `<p>Due Date: ${format(new Date(invoice.due_date), 'MMMM d, yyyy')}</p>` : ''}
      <p>Thank you for your business!</p>
      <p>My Project Agent Team</p>
    `;
    
    try {
      await emailAPI.send({
        recipient_email: invoice.client_email,
        subject: `Invoice ${invoice.invoice_number} from My Project Agent`,
        html_content: htmlContent,
        related_type: 'invoice',
        related_id: invoice.id
      });
      await invoicesAPI.update(invoice.id, { status: 'sent' });
      toast.success(`Invoice sent to ${invoice.client_email}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setFormData({
      job_id: '',
      quote_id: '',
      client_name: '',
      client_email: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      due_date: '',
      notes: '',
    });
  };

  const filteredInvoices = invoices.filter(inv =>
    inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Invoices</h1>
          <p className="text-muted-foreground mt-1">Create and manage invoices</p>
        </div>
        <div className="flex items-center gap-2">
        <Dialog open={importOpen} onOpenChange={(o) => { setImportOpen(o); if (!o) setImportResult(null); }}>
          <DialogTrigger asChild>
            <Button variant="outline" className="font-semibold rounded-lg" data-testid="import-invoice-btn">
              <Upload className="h-4 w-4 mr-2" /> Import invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Import an incoming invoice</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Upload a supplier/trade invoice (PDF, image, Word, Excel). The AI reads it and matches it to the right PO/job automatically.</p>
              <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-xl p-8 cursor-pointer hover:border-primary/50 transition-colors" data-testid="import-invoice-dropzone">
                {importing ? <Loader2 className="h-8 w-8 text-primary animate-spin" /> : <Upload className="h-8 w-8 text-muted-foreground" />}
                <span className="text-sm font-medium">{importing ? 'Reading invoice…' : 'Click to choose a file'}</span>
                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,.txt" onChange={handleImportInvoice} disabled={importing} data-testid="import-invoice-input" />
              </label>
              {importResult && (
                <div className={`rounded-lg border p-3 text-sm ${importResult.matched ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/30 bg-amber-500/5'}`} data-testid="import-invoice-result">
                  <p className="font-semibold">{importResult.matched ? `Matched to ${importResult.job_title}` : 'Imported — no job matched yet'}</p>
                  <p className="text-muted-foreground mt-1">
                    Invoice {importResult.invoice_number || '—'} · {importResult.contractor_name || 'Supplier'} · Total ${Number(importResult.total || 0).toFixed(2)}
                    {importResult.po_number ? ` · PO ${importResult.po_number}` : ''}
                  </p>
                  {importResult.match_status === 'overcharge' && <p className="text-red-400 mt-1">⚠️ Over the PO by ${Number(importResult.variance || 0).toFixed(2)}</p>}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="font-semibold rounded-lg" data-testid="add-invoice-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Quote (Optional)</Label>
                  <Select value={formData.quote_id} onValueChange={handleQuoteChange}>
                    <SelectTrigger className="bg-input border-border rounded-lg">
                      <SelectValue placeholder="Select a quote" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {quotes.filter(q => q.status === 'accepted').map(quote => (
                        <SelectItem key={quote.id} value={quote.id}>{quote.quote_number} - {quote.client_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select Job *</Label>
                  <Select value={formData.job_id} onValueChange={handleJobChange}>
                    <SelectTrigger className="bg-input border-border rounded-lg" data-testid="invoice-job">
                      <SelectValue placeholder="Select a job" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {jobs.map(job => (
                        <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                    className="bg-input border-border rounded-lg"
                    data-testid="invoice-client-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="bg-input border-border rounded-lg"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="bg-input border-border rounded-lg"
                />
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-lg">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2 border border-border rounded-lg p-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="col-span-6 bg-input border-border rounded-lg text-sm"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="col-span-2 bg-input border-border rounded-lg text-sm"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        className="col-span-3 bg-input border-border rounded-lg text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="col-span-1"
                        disabled={formData.items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t border-border">
                    <p className="font-mono font-bold">
                      Subtotal: ${calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-input border-border rounded-lg"
                  rows={3}
                  placeholder="Payment terms, bank details, etc..."
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-lg">Cancel</Button>
                <Button type="submit" className="font-semibold rounded-lg" data-testid="invoice-submit">
                  Create Invoice
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Payments to reconcile (from remittance emails) */}
      {payMatches.length > 0 && (
        <Card className="bg-card border-emerald-500/30 rounded-lg" data-testid="payments-to-reconcile">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="h-5 w-5 text-emerald-400" />
              <h2 className="font-semibold">Payments to reconcile</h2>
              <span className="text-xs text-muted-foreground">({payMatches.length})</span>
            </div>
            <div className="space-y-2">
              {payMatches.map((pm) => (
                <div key={pm.id} className="flex items-center gap-3 p-3 rounded-lg border border-border" data-testid={`payment-match-${pm.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">
                      {pm.remittance_total != null ? `$${Number(pm.remittance_total).toFixed(2)}` : 'Payment'} from {pm.from_address || 'a builder'}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                      {pm.matched_count} of {(pm.payments || []).length} invoice(s) matched:{' '}
                      {(pm.payments || []).map((p) => `${p.invoice_number || '—'} ($${Number(p.amount || 0).toFixed(2)}${p.matched ? '' : ' · unmatched'})`).join(', ')}
                    </p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleDismissPayment(pm.id)} data-testid={`dismiss-payment-${pm.id}`} className="text-muted-foreground hover:text-foreground">Dismiss</Button>
                  <Button size="sm" onClick={() => handleApplyPayment(pm.id)} disabled={applyingPay === pm.id || !pm.matched_count} data-testid={`confirm-payment-${pm.id}`}>
                    {applyingPay === pm.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle className="h-4 w-4 mr-1.5" /> Confirm paid</>}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search invoices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-input border-border rounded-lg"
          data-testid="invoice-search"
        />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4" />
              <p>No invoices found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} className="border-border" data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-mono text-sm">{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.client_name}</TableCell>
                    <TableCell className="font-mono font-bold">
                      <div className="min-w-[110px]">
                        ${invoice.total?.toFixed(2)}
                        {(() => {
                          const total = Number(invoice.total || 0);
                          const paid = invoice.status === 'paid' ? total : Number(invoice.amount_paid || 0);
                          if (total <= 0 || (paid <= 0 && invoice.status !== 'paid')) return null;
                          const pct = Math.max(0, Math.min(100, Math.round((paid / total) * 100)));
                          return (
                            <div className="mt-1" data-testid={`invoice-progress-${invoice.id}`}>
                              <div className="h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                                <div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-emerald-400'}`} style={{ width: `${pct}%` }} />
                              </div>
                              <span className="block text-[10px] font-normal text-muted-foreground mt-0.5">
                                ${paid.toFixed(2)} paid · {pct}%{pct < 100 ? ` · $${(total - paid).toFixed(2)} left` : ''}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </TableCell>
                    <TableCell>
                      {invoice.due_date ? format(new Date(invoice.due_date), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Select value={invoice.status} onValueChange={(v) => handleStatusChange(invoice.id, v)}>
                        <SelectTrigger className={`w-auto h-auto px-2 py-1 border ${statusColors[invoice.status]} rounded-lg text-xs font-mono uppercase`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="partial">Partial</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="overdue">Overdue</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(invoice.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" title="View PDF" onClick={() => handleViewPdf(invoice.id, invoice.invoice_number)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Download PDF" onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Export editable Excel" onClick={() => handleExportExcel(invoice.id, invoice.invoice_number)} data-testid={`invoice-export-excel-${invoice.id}`}>
                          <FileSpreadsheet className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Send Invoice" onClick={() => handleSendInvoice(invoice)}>
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Mark as Paid" onClick={() => handleStatusChange(invoice.id, 'paid')}>
                          <CheckCircle className="h-4 w-4 text-green-400" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Modal */}
      {pdfViewerOpen && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-100 rounded-t-lg">
              <h3 className="text-lg font-semibold text-gray-800">{pdfTitle}</h3>
              <div className="flex items-center gap-2">
                <a 
                  href={pdfUrl} 
                  download={`${pdfTitle}.pdf`}
                  className="inline-flex items-center px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary/90"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Download
                </a>
                <Button variant="ghost" size="icon" onClick={() => { setPdfViewerOpen(false); window.URL.revokeObjectURL(pdfUrl); }} className="text-gray-600 hover:text-gray-800">
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </div>
            <div className="flex-1 bg-gray-200 overflow-hidden">
              <object 
                data={pdfUrl} 
                type="application/pdf"
                className="w-full h-full"
              >
                <div className="flex flex-col items-center justify-center h-full text-gray-600">
                  <p className="mb-4">Unable to display PDF in browser.</p>
                  <a 
                    href={pdfUrl} 
                    download={`${pdfTitle}.pdf`}
                    className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
                  >
                    Download PDF
                  </a>
                </div>
              </object>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicesPage;
